/**
 * telemetryFlush.ts
 *
 * Collects performance telemetry (Web Vitals, custom spans, long tasks, errors)
 * from the debugBus perf/error channels and flushes them in batches to the
 * `perf_telemetry` Supabase table.
 *
 * Designed for load-test observability: lightweight, non-blocking, and
 * fire-and-forget so it never impacts user experience.
 */

import { debugBus, type DebugEvent } from './debugBus';
import supabase from './supabaseClient';

// ---------------------------------------------------------------------------
// Session identity
// ---------------------------------------------------------------------------
const SESSION_ID = crypto.randomUUID();
let resolvedUserId: string | null = null;

export function setTelemetryUserId(uid: string | null) {
  resolvedUserId = uid;
}

export function getTelemetrySessionId() {
  return SESSION_ID;
}

// Diagnostic counters for the perf dashboard
const diagnostics = {
  enqueued: 0,
  flushed: 0,
  flushErrors: 0,
  lastFlushAt: null as string | null,
  lastError: null as string | null,
};

export function getTelemetryDiagnostics() {
  return { ...diagnostics, buffered: buffer.length };
}

// ---------------------------------------------------------------------------
// Buffer
// ---------------------------------------------------------------------------
interface TelemetryRow {
  session_id: string;
  user_id: string | null;
  event_type: string;
  event_name: string;
  value_ms: number | null;
  metadata: Record<string, unknown>;
}

const buffer: TelemetryRow[] = [];
const FLUSH_INTERVAL_MS = 30_000;
const MAX_BATCH = 200;

function enqueue(row: Omit<TelemetryRow, 'session_id' | 'user_id'>) {
  if (!resolvedUserId) return;
  buffer.push({ ...row, session_id: SESSION_ID, user_id: resolvedUserId });
  diagnostics.enqueued++;
}

// ---------------------------------------------------------------------------
// Collect from debugBus
// ---------------------------------------------------------------------------
function ingestPerfEvent(evt: DebugEvent) {
  const detail = (evt.detail ?? {}) as Record<string, unknown>;

  // Perf spans (auth.getSession, context.load, etc.)
  if (evt.phase === 'end' && typeof detail.duration_ms === 'number') {
    enqueue({
      event_type: 'span',
      event_name: evt.message,
      value_ms: detail.duration_ms as number,
      metadata: detail,
    });
    return;
  }

  // Long tasks
  if (evt.message === 'long-task' && typeof detail.duration_ms === 'number') {
    enqueue({
      event_type: 'long-task',
      event_name: 'long-task',
      value_ms: detail.duration_ms as number,
      metadata: detail,
    });
    return;
  }

  // FCP from PerformanceObserver
  if (evt.message === 'FCP' && typeof detail.time_ms === 'number') {
    enqueue({
      event_type: 'web-vital',
      event_name: 'FCP',
      value_ms: detail.time_ms as number,
      metadata: detail,
    });
    return;
  }
}

function ingestErrorEvent(evt: DebugEvent) {
  enqueue({
    event_type: 'error',
    event_name: evt.message?.slice(0, 200) || 'unknown',
    value_ms: null,
    metadata: {
      level: evt.level,
      detail: evt.detail,
      page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    },
  });
}

// ---------------------------------------------------------------------------
// Web Vitals via native PerformanceObserver
// ---------------------------------------------------------------------------
const reportedVitals = new Set<string>();

function observeWebVitals() {
  if (typeof window === 'undefined' || !window.PerformanceObserver) return;

  // LCP
  try {
    const lcpObs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last && !reportedVitals.has('LCP')) {
        reportedVitals.add('LCP');
        enqueue({
          event_type: 'web-vital',
          event_name: 'LCP',
          value_ms: +last.startTime.toFixed(2),
          metadata: { element: (last as any).element?.tagName },
        });
      }
    });
    lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch { /* unsupported */ }

  // CLS
  try {
    let clsValue = 0;
    const clsObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value ?? 0;
        }
      }
    });
    clsObs.observe({ type: 'layout-shift', buffered: true });
    // Report CLS on visibilitychange (final value)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && !reportedVitals.has('CLS')) {
        reportedVitals.add('CLS');
        enqueue({
          event_type: 'web-vital',
          event_name: 'CLS',
          value_ms: +clsValue.toFixed(4),
          metadata: {},
        });
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // INP (Interaction to Next Paint)
  try {
    let worstInp = 0;
    const inpObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const dur = (entry as any).duration ?? 0;
        if (dur > worstInp) worstInp = dur;
      }
    });
    inpObs.observe({ type: 'event', buffered: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && worstInp > 0 && !reportedVitals.has('INP')) {
        reportedVitals.add('INP');
        enqueue({
          event_type: 'web-vital',
          event_name: 'INP',
          value_ms: +worstInp.toFixed(2),
          metadata: {},
        });
      }
    }, { once: true });
  } catch { /* unsupported */ }

  // TTFB
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav && nav.responseStart > 0) {
      enqueue({
        event_type: 'web-vital',
        event_name: 'TTFB',
        value_ms: +(nav.responseStart - nav.requestStart).toFixed(2),
        metadata: { transferSize: nav.transferSize },
      });
    }
  } catch { /* unsupported */ }
}

// ---------------------------------------------------------------------------
// Connection quality snapshot
// ---------------------------------------------------------------------------
function getConnectionInfo(): Record<string, unknown> {
  try {
    const conn = (navigator as any).connection;
    if (!conn) return {};
    return {
      effectiveType: conn.effectiveType,
      downlink: conn.downlink,
      rtt: conn.rtt,
    };
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------
let flushInProgress = false;

async function flush() {
  if (flushInProgress || buffer.length === 0) return;
  flushInProgress = true;
  try {
    const batch = buffer.splice(0, MAX_BATCH);
    // Attach connection info to first row of each batch as a session marker
    if (batch.length > 0) {
      const connInfo = getConnectionInfo();
      if (Object.keys(connInfo).length > 0) {
        batch[0].metadata = { ...batch[0].metadata, connection: connInfo };
      }
    }
    const { error } = await supabase.from('perf_telemetry').insert(batch);
    if (error) {
      // Put rows back if insert failed (best-effort)
      buffer.unshift(...batch);
      diagnostics.flushErrors++;
      diagnostics.lastError = error.message;
      // eslint-disable-next-line no-console
      console.warn('[telemetryFlush] insert failed:', error.message, error);
    } else {
      diagnostics.flushed += batch.length;
      diagnostics.lastFlushAt = new Date().toISOString();
    }
  } catch (e) {
    diagnostics.flushErrors++;
    diagnostics.lastError = String(e);
    // eslint-disable-next-line no-console
    console.warn('[telemetryFlush] flush error:', e);
  } finally {
    flushInProgress = false;
  }
}

// ---------------------------------------------------------------------------
// Install
// ---------------------------------------------------------------------------
let installed = false;

export async function forceFlush() {
  await flush();
}

export function installTelemetryFlush() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Subscribe to debugBus channels
  debugBus.subscribe('perf', (_events, newEvt) => {
    if (newEvt) ingestPerfEvent(newEvt);
  });
  debugBus.subscribe('error', (_events, newEvt) => {
    if (newEvt) ingestErrorEvent(newEvt);
  });

  // Observe native Web Vitals
  observeWebVitals();

  // First flush after a short delay to capture boot-time spans + TTFB/FCP
  setTimeout(flush, 5_000);

  // Periodic flush
  const intervalId = setInterval(flush, FLUSH_INTERVAL_MS);

  // Flush on page hide (user navigating away or closing tab)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });

  // Flush before unload (best-effort)
  window.addEventListener('pagehide', () => {
    flush();
  });

  return () => {
    clearInterval(intervalId);
  };
}
